import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { getAiBudget, type AiBudget } from '@/api/usage';
import { Button } from '@/components/ui/button';

const fmt = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;

/**
 * A thin warning bar shown above the agent's pages when their AI spend is near or over their plan
 * limit. Over-limit means AI building is paused by the server guardrail (402) — this tells them why
 * and how to fix it (upgrade now; wallet credits are the follow-on). Hidden when comfortably under,
 * and when billing is degraded/unreachable (so we never show a misleading warning).
 */
export function AiBudgetBanner() {
  const navigate = useNavigate();
  const { data } = useQuery<AiBudget>({
    queryKey: ['ai-budget'],
    queryFn: getAiBudget,
    staleTime: 60_000,
    refetchInterval: 120_000,
    retry: false,
  });

  if (!data || data.degraded) return null;

  const { ai_used_usd, ai_limit_usd, is_over_limit } = data;
  const pct = ai_limit_usd > 0 ? ai_used_usd / ai_limit_usd : is_over_limit ? 1 : 0;
  const near = !is_over_limit && pct >= 0.8;
  if (!is_over_limit && !near) return null;

  return (
    <div
      className={`flex items-center justify-between gap-3 border-b px-4 py-2 text-sm ${
        is_over_limit
          ? 'border-destructive/30 bg-destructive/10 text-destructive'
          : 'border-brand-sand-deep/30 bg-brand-sand-deep/10 text-brand-sand-deep'
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        {is_over_limit ? (
          <span className="truncate">
            You've reached your AI limit ({fmt(ai_used_usd)} of {fmt(ai_limit_usd)}). AI building is paused —
            upgrade your plan or add credits to keep going. Your site stays live.
          </span>
        ) : (
          <span className="truncate">
            You've used {fmt(ai_used_usd)} of your {fmt(ai_limit_usd)} monthly AI limit ({Math.round(pct * 100)}%).
            Upgrade or add credits before you run out.
          </span>
        )}
      </div>
      <Button size="sm" variant={is_over_limit ? 'destructive' : 'default'} className="shrink-0" onClick={() => navigate('/billing')}>
        Upgrade / add credits
      </Button>
    </div>
  );
}
